import { useMemo } from 'react';
import { AlertTriangle, FileCheck } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { Badge } from '@/components/Shared/Badge';
import { formatScheduleRule } from '@/utils/scheduleRuleConstants';
import { formatMoney } from '@/utils/money';
import { specToFormattable } from '@/pages/Import/Contract/logic/mapExtraction';
import { CONTRACT_MAX_FILE_MB } from '@/pages/Import/Contract/logic/fileLimits';
import { BillingKinds, SELECTABLE_BILLING_KINDS, billingKindLabelKey, formatBillingRule } from '@/utils/billingKind';
import { numberLocale } from '@/utils/locale';
import { useContractImportStore } from '@/store/useContractImportStore';
import { ContractStepFooter } from './ContractStepFooter';
import { Kicker } from './wizardUi';

export function SummaryStep() {
  const { t } = useTranslation('contractImport');
  const {
    customer, contact, object, positions, offer, live,
    commit, committing, error, commitIssues,
  } = useContractImportStore();

  const included = useMemo(() => positions.filter((p) => p.include), [positions]);
  const issues = commitIssues();

  const customerLabel =
    customer.mode === 'link'
      ? live.customers.find((c) => c.id === customer.existingCustomerId)?.name ?? ''
      : customer.name;
  const objectLabel =
    object.mode === 'link'
      ? live.objects.find((o) => o.id === object.existingObjectId)?.name ?? ''
      : object.name;

  const serviceLabel = (p: (typeof positions)[number]) =>
    p.service.mode === 'link'
      ? live.services.find((s) => s.id === p.service.existingServiceId)?.name ?? ''
      : p.service.name;

  // why: one line per billing kind in use; monthly always, as the headline amount.
  const totals = useMemo(
    () =>
      [...SELECTABLE_BILLING_KINDS, BillingKinds.Unknown]
        .map((kind) => ({
          kind,
          amount: included.filter((p) => p.recurrenceKind === kind).reduce((acc, p) => acc + p.netAmount, 0),
          used: included.some((p) => p.recurrenceKind === kind),
        }))
        .filter((row) => row.kind === BillingKinds.Monthly || row.used),
    [included],
  );

  const modeBadge = (mode: 'link' | 'create') => (
    <Badge variant={mode === 'link' ? 'info' : 'success'}>
      {mode === 'link' ? t('summary.linked') : t('summary.created')}
    </Badge>
  );

  return (
    <div className="space-y-4">
      <Card>
        <div className="space-y-4">
          <p className="text-[13px] text-slate-600">{t('summary.hint')}</p>

          {/* Entities */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-200 px-3 py-2.5 space-y-1">
              <Kicker>{t('summary.customer')}</Kicker>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-slate-900 truncate">{customerLabel || '—'}</span>
                {modeBadge(customer.mode)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 px-3 py-2.5 space-y-1">
              <Kicker>{t('summary.contact')}</Kicker>
              <div className="text-[13px] text-slate-900 truncate">
                {contact.enabled
                  ? `${contact.firstName} ${contact.lastName}`.trim() || '—'
                  : t('summary.contactNone')}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 px-3 py-2.5 space-y-1">
              <Kicker>{t('summary.object')}</Kicker>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-slate-900 truncate">{objectLabel || '—'}</span>
                {modeBadge(object.mode)}
              </div>
            </div>
          </div>

          {/* Positions */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.4fr)_90px_minmax(0,1fr)] gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">
              <span>{t('summary.colPosition')}</span>
              <span>{t('summary.colSchedule')}</span>
              <span>{t('summary.colChecklist')}</span>
              <span className="text-right">{t('summary.colPrice')}</span>
            </div>
            <div className="divide-y divide-slate-100">
              {included.map((p) => (
                <div
                  key={p.key}
                  className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.4fr)_90px_minmax(0,1fr)] gap-3 px-3 py-2 items-start"
                >
                  <div className="min-w-0">
                    <div className="text-[13px] text-slate-900 truncate">{p.offerTitle}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {serviceLabel(p)}
                      {p.service.mode === 'create' && <span className="ml-1">({t('summary.created')})</span>}
                    </div>
                  </div>
                  <div className="text-[12px] text-slate-600 space-y-0.5">
                    {p.scheduleRules.length === 0 && <span className="text-slate-400">—</span>}
                    {p.scheduleRules.map((r) => (
                      <div key={r.id} className="truncate">{formatScheduleRule(specToFormattable(r))}</div>
                    ))}
                  </div>
                  <div className="text-[12px] text-slate-600 tabular-nums">
                    {t('summary.checklistCount', { count: p.checklistItems.filter((c) => c.title.trim()).length })}
                  </div>
                  <div className="text-right">
                    <div className="text-[13px] text-slate-900 tabular-nums">{formatMoney(p.netAmount)}</div>
                    <div className="text-[11px] text-slate-500">
                      {[
                        t(billingKindLabelKey(p.recurrenceKind)),
                        formatBillingRule(p.recurrenceKind, p.billingRule, numberLocale(), t('offers:billing.inArrearsShort')),
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              ))}
              {included.length === 0 && (
                <p className="px-3 py-6 text-center text-[12px] text-slate-500">{t('positions.empty')}</p>
              )}
            </div>
          </div>

          {/* Offer totals */}
          <div className="rounded-xl border border-slate-200 px-3 py-2.5 space-y-1.5">
            <Kicker>{t('summary.totals.title')}</Kicker>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[13px]">
              {totals.map(({ kind, amount }) => (
                <div key={kind} className="flex justify-between gap-3">
                  <span className="text-slate-600">
                    {t('summary.totals.byKind', { kind: t(billingKindLabelKey(kind)) })}
                  </span>
                  <span className="text-slate-900 tabular-nums font-medium">{formatMoney(amount)}</span>
                </div>
              ))}
              <div className="flex justify-between gap-3">
                <span className="text-slate-600">{t('summary.totals.taxRate')}</span>
                <span className="text-slate-900 tabular-nums">{offer.taxRate}%</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-600">{t('summary.totals.offerDate')}</span>
                <span className="text-slate-900 tabular-nums">{offer.offerDate}</span>
              </div>
              {offer.paymentTerms && (
                <div className="flex justify-between gap-3">
                  <span className="text-slate-600">{t('summary.totals.paymentTerms')}</span>
                  <span className="text-slate-900 truncate">{offer.paymentTerms}</span>
                </div>
              )}
            </div>
          </div>

          {/* What the commit does */}
          <div className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-[12px] text-sky-800">
            <FileCheck className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
            <div className="space-y-0.5">
              <div>{t('summary.notice.accepted')}</div>
              <div>{t('summary.notice.invoice')}</div>
              <div>{t('summary.notice.document')}</div>
            </div>
          </div>

          {/* Blocking issues */}
          {issues.length > 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
              <div className="space-y-0.5">
                <div className="font-medium">{t('summary.issues.title')}</div>
                {issues.map((issue, i) => (
                  <div key={i}>{t(`summary.issues.${issue.key}`, issue.params)}</div>
                ))}
              </div>
            </div>
          )}

          {/* Commit failure */}
          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[12px] text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
              <span>{t(error, { maxMb: CONTRACT_MAX_FILE_MB })}</span>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => commit()} disabled={issues.length > 0} loading={committing}>
              {committing ? t('summary.committing') : t('actions.commit')}
            </Button>
          </div>
        </div>
      </Card>

      <ContractStepFooter back="positions" />
    </div>
  );
}

export default SummaryStep;
